import { useEffect, useState } from 'react';
import { matchPath } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import useAuth from '../components/Auth';
import { entriesSetState, selectAllEntries, selectIsEntriesHydrated } from '../containers/contentSlice';
import {
	localStorageGetIsAboutOnboardingCompleted,
	localStorageGetIsIntroDialogCompleted,
	localStorageSetIsAboutOnboardingCompleted,
	localStorageSetIsIntroDialogCompleted,
	sessionStorageGetIsAboutOnboardingCompleted,
	sessionStorageSetIsAboutOnboardingCompleted,
} from '../localStorage';
import { useEntryImportMutation, useLoadEntryQuery } from '../services/records';
import { entries as BACKLOG_ENTRIES } from '../dataPresets/backlog';
import { entries as ROADMAP_ENTRIES } from '../dataPresets/roadmap';
import { entries as ENGINEERING_ENTRIES } from '../dataPresets/engineering';
import { entries as JOURNAL_ENTRIES } from '../dataPresets/journal';
import { entries as BRACELETS_ENTRIES } from '../dataPresets/goalTracker';

/** True for this full page load when the initial URL had a non-empty `demo` query (survives Strict Mode remounts). */
let landingPageLoadHadDemoSearchParam = false;

export function getLandingPageLoadHadDemoSearchParam(): boolean {
	return landingPageLoadHadDemoSearchParam;
}

/**
 * Runs once at module load (before any child `useEffect`), so `ContentWrapper` sees intro as
 * completed when opening the app with `?demo=…` and does not flash the intro dialog.
 */
function syncIntroCompletedFromLandingDemoSearchParam() {
	if (typeof window === 'undefined') {
		return;
	}
	let demoParam: string | null;
	try {
		demoParam = new URL(window.location.href).searchParams.get('demo');
	} catch {
		return;
	}
	if (!demoParam) {
		return;
	}
	landingPageLoadHadDemoSearchParam = true;
	localStorageSetIsIntroDialogCompleted(true);
}

syncIntroCompletedFromLandingDemoSearchParam();

/**
 * Onboarding + landing flow decisions:
 *
 * 1) Parse URL params (`from`, `demo`) to detect landing/demo entry and persist intro completion
 *    when `demo` is present (also via a sync module initializer before React effects), plus session /
 *    local storage for about-onboarding once cookie consent is completed.
 * 2) Support direct `/replicate/:repositoryId` links by marking about-onboarding completed,
 *    preserving the existing persisted onboarding state for that route.
 * 3) Resolve the onboarding gate without redirecting first-time users away from the app.
 * 4) Keep session onboarding state synced to local storage once cookie prompt is completed.
 * 5) Handle landing demo import only after onboarding redirect is resolved and the workspace is ready:
 *    - Unauthenticated: wait for local entries hydration.
 *    - Authenticated: wait for the root `loadEntry` query so cloud data is known.
 *    - If workspace is empty, auto-import selected demo (authenticated or not).
 *    - If workspace has data, open a confirmation dialog (offline: wipe or keep; cloud: clear-first message).
 *
 * Important guard: demo import decisions wait for workspace readiness to avoid the startup race
 * where an initial empty Redux state could overwrite local storage before hydration.
 */
export type LandingDemo = 'backlog' | 'roadmap' | 'engineering' | 'journal' | 'bracelets';

const LANDING_DEMO_REQUEST_EVENT = 'untwyn:landing-demo-request';

export function requestLandingDemoImport(demo: LandingDemo) {
	window.dispatchEvent(new CustomEvent<{demo: LandingDemo}>(LANDING_DEMO_REQUEST_EVENT, {detail: {demo}}));
}

type UseOnboardingFlowArgs = {
	cookiePromptCompleted: boolean;
};

export type LandingDemoDecisionVariant = 'offline' | 'cloud';

type UseOnboardingFlowResult = {
	isOnboardingRedirectResolved: boolean;
	isLandingDemoDecisionOpen: boolean;
	landingDemoDecisionVariant: LandingDemoDecisionVariant | null;
	keepWorkspace: () => void;
	wipeWorkspaceAndLoadDemo: () => void;
};

export default function useOnboardingFlow({
	cookiePromptCompleted,
}: UseOnboardingFlowArgs): UseOnboardingFlowResult {
	const [isOnboardingRedirectResolved, setIsOnboardingRedirectResolved] = useState(false);
	const [landingDemo, setLandingDemo] = useState<LandingDemo | null>(null);
	const [isLandingDemoDecisionOpen, setIsLandingDemoDecisionOpen] = useState(false);
	const [isLandingDemoHandled, setIsLandingDemoHandled] = useState(false);
	const dispatch = useAppDispatch();
	const allEntries = useAppSelector(selectAllEntries);
	const isEntriesHydrated = useAppSelector(selectIsEntriesHydrated);
	const [entryImportMutation] = useEntryImportMutation();
	const { isAuthenticated, isAuthenticating } = useAuth();
	const { isSuccess: isRootEntryLoaded } = useLoadEntryQuery(null, {
		skip: isAuthenticating || !isAuthenticated,
	});
	const isWorkspaceReady = isAuthenticated ? isRootEntryLoaded : isEntriesHydrated;

	useEffect(() => {
		const url = new URL(window.location.href);
		const fromLanding = url.searchParams.get('from') === 'landing';
		const landingDemoParam = url.searchParams.get('demo');
		const hasLandingDemoParam = Boolean(landingDemoParam);
		if (landingDemoParam === 'backlog') {
			setLandingDemo('backlog');
		}
		if (landingDemoParam === 'roadmap') {
			setLandingDemo('roadmap');
		}
		if (landingDemoParam === 'engineering') {
			setLandingDemo('engineering');
		}
		if (landingDemoParam === 'journal') {
			setLandingDemo('journal');
		}
		if (landingDemoParam === 'bracelets') {
			setLandingDemo('bracelets');
		}

		if (fromLanding || hasLandingDemoParam) {
			sessionStorageSetIsAboutOnboardingCompleted(true);
			if (cookiePromptCompleted) {
				localStorageSetIsAboutOnboardingCompleted(true);
			}

			url.searchParams.delete('from');
			url.searchParams.delete('demo');
			const cleanSearch = url.searchParams.toString();
			const cleanUrl = `${url.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${url.hash}`;
			window.history.replaceState({}, document.title, cleanUrl);
		}

		// Preserve the prior onboarding marker for direct replicate links.
		if (matchPath({ path: '/replicate/:repositoryId', end: true }, url.pathname)) {
			sessionStorageSetIsAboutOnboardingCompleted(true);
			if (cookiePromptCompleted) {
				localStorageSetIsAboutOnboardingCompleted(true);
			}
		}

		const hasSessionAboutOnboarding = sessionStorageGetIsAboutOnboardingCompleted();
		const hasPersistentAboutOnboarding = localStorageGetIsAboutOnboardingCompleted();
		const hasCompletedIntroDialog = localStorageGetIsIntroDialogCompleted();

		// Backward compatibility: users who already completed intro should bypass about gate.
		if (hasCompletedIntroDialog) {
			if (!hasSessionAboutOnboarding) {
				sessionStorageSetIsAboutOnboardingCompleted(true);
			}
			if (cookiePromptCompleted && !hasPersistentAboutOnboarding) {
				localStorageSetIsAboutOnboardingCompleted(true);
			}
			setIsOnboardingRedirectResolved(true);
			return;
		}

		setIsOnboardingRedirectResolved(true);
	}, [cookiePromptCompleted]);

	useEffect(() => {
		if (!cookiePromptCompleted) {
			return;
		}
		const hasSessionAboutOnboarding = sessionStorageGetIsAboutOnboardingCompleted();
		const hasPersistentAboutOnboarding = localStorageGetIsAboutOnboardingCompleted();
		if (hasSessionAboutOnboarding && !hasPersistentAboutOnboarding) {
			localStorageSetIsAboutOnboardingCompleted(true);
		}
	}, [cookiePromptCompleted]);

	useEffect(() => {
		const handleLandingDemoRequest = (event: Event) => {
			const demo = (event as CustomEvent<{demo?: LandingDemo}>).detail?.demo;
			if (
				demo !== 'backlog' &&
				demo !== 'roadmap' &&
				demo !== 'engineering' &&
				demo !== 'journal' &&
				demo !== 'bracelets'
			) {
				return;
			}
			sessionStorageSetIsAboutOnboardingCompleted(true);
			if (cookiePromptCompleted) {
				localStorageSetIsAboutOnboardingCompleted(true);
			}
			setLandingDemo(demo);
			setIsLandingDemoHandled(false);
			setIsLandingDemoDecisionOpen(false);
		};

		window.addEventListener(LANDING_DEMO_REQUEST_EVENT, handleLandingDemoRequest);
		return () => {
			window.removeEventListener(LANDING_DEMO_REQUEST_EVENT, handleLandingDemoRequest);
		};
	}, [cookiePromptCompleted]);

	const clearLandingDemoQueryParams = () => {
		const url = new URL(window.location.href);
		url.searchParams.delete('demo');
		url.searchParams.delete('from');
		const cleanSearch = url.searchParams.toString();
		const cleanUrl = `${url.pathname}${cleanSearch ? `?${cleanSearch}` : ''}${url.hash}`;
		window.history.replaceState({}, document.title, cleanUrl);
	};

	const runLandingDemoImport = ({ wipe = false }: { wipe?: boolean } = {}) => {
		if (isAuthenticating) {
			return;
		}
		if (isAuthenticated && allEntries.length > 0) {
			return;
		}
		localStorageSetIsIntroDialogCompleted(true);
		if (wipe) {
			dispatch(entriesSetState([]));
		}
		if (landingDemo === 'backlog') {
			entryImportMutation(BACKLOG_ENTRIES as any);
		} else if (landingDemo === 'roadmap') {
			entryImportMutation(ROADMAP_ENTRIES as any);
		} else if (landingDemo === 'engineering') {
			entryImportMutation(ENGINEERING_ENTRIES as any);
		} else if (landingDemo === 'journal') {
			entryImportMutation(JOURNAL_ENTRIES as any);
		} else if (landingDemo === 'bracelets') {
			entryImportMutation(BRACELETS_ENTRIES as any);
		}
		clearLandingDemoQueryParams();
		setIsLandingDemoDecisionOpen(false);
		setIsLandingDemoHandled(true);
	};

	useEffect(() => {
		if (isLandingDemoHandled || !landingDemo) {
			return;
		}
		if (!isOnboardingRedirectResolved || isAuthenticating || !isWorkspaceReady) {
			return;
		}
		const hasEntries = allEntries.length > 0;
		if (!hasEntries) {
			runLandingDemoImport();
			return;
		}
		setIsLandingDemoDecisionOpen(true);
	}, [
		landingDemo,
		isLandingDemoHandled,
		isOnboardingRedirectResolved,
		isAuthenticated,
		isAuthenticating,
		isWorkspaceReady,
		allEntries,
	]);

	const keepWorkspace = () => {
		localStorageSetIsIntroDialogCompleted(true);
		clearLandingDemoQueryParams();
		setIsLandingDemoDecisionOpen(false);
		setIsLandingDemoHandled(true);
	};

	const wipeWorkspaceAndLoadDemo = () => {
		runLandingDemoImport({ wipe: true });
	};

	const landingDemoDecisionVariant: LandingDemoDecisionVariant | null = isLandingDemoDecisionOpen
		? isAuthenticated
			? 'cloud'
			: 'offline'
		: null;

	return {
		isOnboardingRedirectResolved,
		isLandingDemoDecisionOpen,
		landingDemoDecisionVariant,
		keepWorkspace,
		wipeWorkspaceAndLoadDemo,
	};
}
